import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { payWithRazorpay } from "../lib/razorpay";
import { X, Zap, Loader2, History, CreditCard, FlaskConical } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const gradient = { background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" };
const LABEL = { purchase: "Purchase", admin_adjust: "Adjustment", refund: "Refund", text: "Chat", voice: "Voice", image: "Image", video: "Video", learn: "Lesson", agent: "Agent" };

function PayButton({ busy, onPay, demo, testid }) {
  return (
    <button data-testid={testid} onClick={onPay} disabled={busy}
      className="rounded-full px-4 py-2 text-sm text-[#000000] font-medium flex items-center gap-2 disabled:opacity-60 shrink-0" style={gradient}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : demo ? <FlaskConical className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />} {demo ? "Get (demo)" : "Buy"}
    </button>
  );
}

export function WalletModal({ open, onClose, balance, onBalance }) {
  const [w, setW] = useState(null);
  const [txns, setTxns] = useState([]);
  const [buying, setBuying] = useState("");
  const [tab, setTab] = useState("buy");
  const [custom, setCustom] = useState(10);

  const loadTxns = useCallback(() => api.get("/transactions").then(({ data }) => setTxns(data.transactions)).catch(() => {}), []);
  useEffect(() => {
    if (!open) return;
    api.get("/wallet").then(({ data }) => {
      setW(data); onBalance(data.balance);
      if (data.payments_enabled) {
        api.post("/payments/reconcile").then(({ data: r }) => {
          if (r.credited > 0) { onBalance(r.balance); loadTxns(); toast.success("A completed payment was confirmed — credits added."); }
        }).catch(() => {});
      }
    }).catch(() => {});
    loadTxns();
  }, [open, onBalance, loadTxns]);

  if (!open) return null;
  const real = w?.payments_enabled;
  const cc = w?.custom_credit;

  const pay = async (key, target, demo) => {
    setBuying(key);
    try {
      if (demo) {
        const { data } = await api.post("/wallet/purchase", target.packageId ? { package_id: target.packageId } : { credits: target.credits });
        onBalance(data.balance); toast.success(`Added ${data.granted} demo credits.`);
      } else {
        const r = await payWithRazorpay(target);
        if (r.status === "paid") { onBalance(r.balance); toast.success(`Payment successful — ${r.credits} credits added. New balance ${r.balance}.`); }
        else if (r.status === "cancelled") toast.message("Payment cancelled — no money was taken and no credits were added.");
        else toast.error(`Payment failed: ${r.message}${/no credits/i.test(r.message || "") ? "" : " No credits were added."}`);
      }
      loadTxns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the payment. Please try again.");
    } finally { setBuying(""); }
  };

  const customN = Math.max(cc?.min || 1, Math.min(cc?.max || 5000, parseInt(custom, 10) || 0));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="wallet-modal" className="w-full max-w-lg glass-panel rounded-3xl overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="label-mono">Stealth Credits</div>
            <div className="flex items-center gap-2 mt-1"><Zap className="w-5 h-5 text-mono-300" /><span className="font-heading text-2xl font-medium" data-testid="wallet-balance">{balance}</span></div>
          </div>
          <button data-testid="wallet-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 p-3 border-b border-white/10">
          {[["buy", "Buy credits"], ["history", "History"]].map(([k, l]) => (
            <button key={k} data-testid={`wallet-tab-${k}`} onClick={() => setTab(k)}
              className={`text-sm px-4 py-2 rounded-full ${tab === k ? "text-[#000000] font-medium" : "text-slate-400"}`}
              style={tab === k ? gradient : {}}>{l}</button>
          ))}
        </div>
        <div className="p-5 overflow-y-auto thin-scroll flex-1 space-y-3">
          {tab === "buy" && w && (
            <>
              {w.free_image && (
                <div data-testid="wallet-free-image" className={`rounded-xl px-4 py-2.5 text-sm border ${w.free_image.available ? "border-mono-400/40 bg-mono-400/5 text-mono-200" : "border-white/10 text-slate-400"}`}>
                  {w.free_image.available ? "✦ You have a free image available today" : `Next free image ${w.free_image.next_at ? new Date(w.free_image.next_at).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "in ~24h"}`}
                </div>
              )}
              <p className="text-xs text-slate-500" data-testid="wallet-pricing-note">
                Chat is free. {w.pricing?.image ? `Images cost ${w.pricing.image} credits after your free daily image.` : ""}
              </p>
              {!real && <p className="text-xs text-amber-300/90" data-testid="wallet-demo-note">Payments aren't switched on yet — packs are added as demo credits for now.</p>}
              {w.packages.map((p) => (
                <div key={p.id} data-testid={`wallet-package-${p.id}`} className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 border border-white/10">
                  <div>
                    <div className="text-sm font-medium">{p.name} · {p.credits} credits</div>
                    <div className="text-xs text-slate-400" data-testid={`wallet-price-${p.id}`}>{inr(p.price_inr)}</div>
                  </div>
                  <div className="flex gap-2">
                    {real && <PayButton testid={`wallet-buy-${p.id}`} busy={buying === p.id} onPay={() => pay(p.id, { packageId: p.id })} />}
                    {w.demo_purchase && <PayButton demo testid={real ? `wallet-demo-${p.id}` : `wallet-buy-${p.id}`} busy={buying === `demo-${p.id}`} onPay={() => pay(`demo-${p.id}`, { packageId: p.id }, true)} />}
                  </div>
                </div>
              ))}
              {cc?.price_inr_per_credit > 0 && (
                <div data-testid="wallet-custom" className="rounded-xl px-4 py-3 border border-white/10 space-y-2">
                  <div className="text-sm font-medium">Buy any amount <span className="text-xs text-slate-400 font-normal">· {inr(cc.price_inr_per_credit)} per credit</span></div>
                  <div className="flex items-center gap-2">
                    <input data-testid="wallet-custom-credits" type="number" min={cc.min} max={cc.max} value={custom} onChange={(e) => setCustom(e.target.value)}
                      className="w-24 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
                    <span className="text-sm text-slate-300 flex-1" data-testid="wallet-custom-total">credits = {inr(customN * cc.price_inr_per_credit)}</span>
                    {real && <PayButton testid="wallet-custom-buy" busy={buying === "custom"} onPay={() => pay("custom", { credits: customN })} />}
                    {w.demo_purchase && <PayButton demo testid={real ? "wallet-custom-demo" : "wallet-custom-buy"} busy={buying === "demo-custom"} onPay={() => pay("demo-custom", { credits: customN }, true)} />}
                  </div>
                  <div className="text-[11px] text-slate-500">Between {cc.min} and {cc.max} credits.</div>
                </div>
              )}
              {real && <p className="text-[11px] text-slate-500">Secure payment by Razorpay. Credits are added as soon as the payment is confirmed.</p>}
            </>
          )}
          {tab === "history" && (
            <div className="space-y-2" data-testid="wallet-transactions">
              {txns.length === 0 && <div className="text-sm text-slate-500">No transactions yet.</div>}
              {txns.map((t) => (
                <div key={t.txn_id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 bg-white/5" data-testid="wallet-txn-row">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><History className="w-3.5 h-3.5 text-slate-500" /><span className="text-sm">{LABEL[t.reason] || t.reason}{t.meta?.demo ? " (demo)" : ""}</span></div>
                    <div className="text-[11px] text-slate-500 ml-5">{new Date(t.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}{t.meta?.amount_inr ? ` · ${inr(t.meta.amount_inr)}` : ""}</div>
                  </div>
                  <span className={`text-sm shrink-0 ${t.type === "credit" ? "text-mono-300" : "text-slate-300"}`}>{t.type === "credit" ? "+" : "−"}{t.amount}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
